import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";

function money(cents) {
  return `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// PayPal redirects the buyer to /orders/return?token=<order_id>&PayerID=<payer>
// after they approve. This component auto-captures and forwards to /orders/:order_id.
export function OrderReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("Confirming your order with PayPal…");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const orderId = params.get("token");
    if (!orderId) {
      setMsg("Missing order token. Head back to the products page and try again.");
      setFailed(true);
      return;
    }
    api.post(`/payments/paypal/capture/${orderId}`)
      .then(() => navigate(`/orders/${orderId}`, { replace: true }))
      .catch((err) => {
        setMsg(formatApiError(err.response?.data?.detail) || "We could not confirm this order.");
        setFailed(true);
      });
  }, [params, navigate]);

  return (
    <Layout>
      <div className="max-w-[700px] mx-auto px-6 py-32 text-center">
        <div className="w-10 h-10 mx-auto mb-8 relative">
          <div className="absolute inset-0 rounded-full border-2 border-[#27272a]" />
          {!failed && (
            <div className="absolute inset-0 rounded-full border-2 border-t-[#ff5a00] animate-spin" />
          )}
        </div>
        <p data-testid="order-return-msg" className="font-mono text-xs uppercase tracking-[0.32em] text-[#a1a1aa]">
          {msg}
        </p>
        {failed && (
          <Link
            to="/products"
            className="inline-block mt-8 font-mono text-[11px] uppercase tracking-[0.32em] text-[#ff5a00] hover:text-[#ff8f00]"
          >
            ← Back to Products
          </Link>
        )}
      </div>
    </Layout>
  );
}

// The receipt page — buyers can bookmark this URL for their records.
export function OrderReceipt() {
  const { orderId } = useParams();
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/payments/status/${orderId}`)
      .then((r) => setTx(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Order not found."));
  }, [orderId]);

  if (err) {
    return (
      <Layout>
        <div className="max-w-[700px] mx-auto px-6 py-32 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-[#a1a1aa]">{err}</p>
          <Link
            to="/products"
            className="inline-block mt-8 font-mono text-[11px] uppercase tracking-[0.32em] text-[#ff5a00] hover:text-[#ff8f00]"
          >
            ← Back to Products
          </Link>
        </div>
      </Layout>
    );
  }

  if (!tx) {
    return (
      <Layout>
        <div className="max-w-[700px] mx-auto px-6 py-32 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-[#a1a1aa]">Loading…</p>
        </div>
      </Layout>
    );
  }

  const paid = tx.payment_status === "paid";

  return (
    <Layout>
      <section
        data-testid="order-receipt"
        className="relative min-h-[80vh] pt-24 pb-24"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] opacity-[0.08] blur-3xl"
               style={{ background: "radial-gradient(circle,#ff5a00,transparent 60%)" }} />
        </div>

        <div className="relative z-10 max-w-[820px] mx-auto px-6 sm:px-10">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00]"
          >
            {paid ? "Receipt · Paid" : "Order · Pending"}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            data-testid="receipt-headline"
            className="font-display uppercase leading-[0.92] mt-6 text-[#ededed]"
            style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}
          >
            {paid ? (
              <>
                Order received.
                <br />
                <span className="italic text-[#ff5a00]">The forge is on it.</span>
              </>
            ) : (
              <>
                Order pending.
                <br />
                <span className="italic text-[#a1a1aa]">PayPal is still processing.</span>
              </>
            )}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-12 border border-[#27272a] bg-[#0b0b0d]/70 backdrop-blur"
          >
            <div className="px-6 sm:px-10 py-6 border-b border-[#27272a] flex flex-wrap justify-between gap-4 items-center">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">Order ID</p>
                <p data-testid="receipt-order-id" className="font-mono text-xs text-[#ededed] mt-1 break-all">{tx.order_id}</p>
              </div>
              <span
                data-testid="receipt-status"
                className={`font-mono text-[10px] uppercase tracking-[0.32em] px-3 py-1.5 border ${
                  paid ? "text-[#ff5a00] border-[#ff5a00]/50" : "text-[#a1a1aa] border-[#27272a]"
                }`}
              >
                {tx.payment_status || tx.status}
              </span>
            </div>

            <div className="px-6 sm:px-10 py-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">Product</p>
              <h3 className="font-display text-3xl text-[#ededed] mt-2 leading-tight">{tx.product_name}</h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mt-1">
                {tx.lookup_key} · qty {tx.quantity}
              </p>
            </div>

            <div className="px-6 sm:px-10 py-6 border-t border-[#27272a] flex flex-wrap justify-between gap-4 items-baseline">
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">Amount ({tx.currency})</p>
              <p data-testid="receipt-amount" className="font-display text-4xl text-[#ff5a00] leading-none">
                {money(tx.amount_cents)}
              </p>
            </div>

            <div className="px-6 sm:px-10 py-6 border-t border-[#27272a]">
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
                Your order is on the record
              </p>
              <p data-testid="receipt-pledge" className="font-display italic text-2xl text-[#ededed] mt-2">
                10% of Resilience net revenue is pledged annually to the causes below.
              </p>
              <Link
                to="/#ledger"
                className="inline-block mt-3 font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] hover:text-[#ff8f00]"
              >
                See the pledge →
              </Link>
            </div>
          </motion.div>

          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] text-center mt-10">
            Save or bookmark this page — it is your receipt on record.
            <br />
            An emailed copy will follow once our email rail is live.
          </p>

          <div className="mt-10 text-center">
            <Link
              to="/products"
              data-testid="receipt-back"
              className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#ededed] border border-[#27272a] px-6 py-3 hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
            >
              Back to Products
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
